import React from 'react';
import PropTypes from 'prop-types';
import { Card, Progress, Table } from 'antd';
import { StarFilled } from '@ant-design/icons';

const BellCurveChart = ({ bellCurveData, loading }) => {
  if (!bellCurveData || bellCurveData.totalEmployees === 0) {
    return (
      <Card title="Performance Bell Curve" loading={loading}>
        <div className="text-center">
          <p>No completed appraisals available for bell curve analysis</p>
        </div>
      </Card>
    );
  }

  const { totalEmployees, distribution, distributionPercentages } = bellCurveData;

  const bellCurveCategories = [
    {
      category: 'Top Performers',
      rating: 5,
      count: distribution.topPerformers,
      percentage: distributionPercentages.topPerformers,
      color: '#52c41a'
    },
    {
      category: 'High Performers',
      rating: 4,
      count: distribution.highPerformers,
      percentage: distributionPercentages.highPerformers,
      color: '#1890ff'
    },
    {
      category: 'Meets Expectations',
      rating: 3,
      count: distribution.meetsExpectations,
      percentage: distributionPercentages.meetsExpectations,
      color: '#faad14'
    },
    {
      category: 'Below Expectations',
      rating: 2,
      count: distribution.belowExpectations,
      percentage: distributionPercentages.belowExpectations,
      color: '#fa8c16'
    },
    {
      category: 'Poor Performers',
      rating: 1,
      count: distribution.poorPerformers,
      percentage: distributionPercentages.poorPerformers,
      color: '#f5222d'
    }
  ];

  const columns = [
    {
      title: 'Performance Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating) => (
        <div>
          {[...Array(rating)].map((_, i) => (
            <StarFilled key={i} style={{ color: '#fadb14', marginRight: 2 }} />
          ))}
        </div>
      )
    },
    {
      title: '% of Employees',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage) => `${percentage}%`
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count'
    },
    {
      title: 'Distribution',
      dataIndex: 'percentage',
      key: 'distribution',
      render: (percentage, record) => (
        <Progress
          percent={percentage}
          strokeColor={record.color}
          showInfo={false}
          size="small"
        />
      )
    }
  ];

  return (
    <Card title={`Performance Bell Curve (Total Employees: ${totalEmployees})`} loading={loading}>
      {/* <div style={{ marginBottom: 20 }}>
        <p>
          This distribution follows a bell curve model where employee performance is distributed across five categories:
        </p>
      </div> */}
      
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'end', height: 200 }}>
          {bellCurveCategories.map((category, index) => (
            <div key={index} style={{ margin: '0 10px', textAlign: 'center' }}>
              <div
                style={{
                  width: 60,
                  height: category.percentage * 3,
                  backgroundColor: category.color,
                  borderRadius: '4px 4px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  minHeight: 30
                }}
              >
                {category.percentage}%
              </div>
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 'bold' }}>
                {category.rating}★
              </div>
              <div style={{ fontSize: 10, color: '#666' }}>
                {category.category}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={bellCurveCategories}
        pagination={false}
        size="small"
        rowKey="category"
      />
    </Card>
  );
};

BellCurveChart.propTypes = {
  bellCurveData: PropTypes.shape({
    totalEmployees: PropTypes.number.isRequired,
    distribution: PropTypes.shape({
      topPerformers: PropTypes.number.isRequired,
      highPerformers: PropTypes.number.isRequired,
      meetsExpectations: PropTypes.number.isRequired,
      belowExpectations: PropTypes.number.isRequired,
      poorPerformers: PropTypes.number.isRequired,
    }).isRequired,
    distributionPercentages: PropTypes.shape({
      topPerformers: PropTypes.number.isRequired,
      highPerformers: PropTypes.number.isRequired,
      meetsExpectations: PropTypes.number.isRequired,
      belowExpectations: PropTypes.number.isRequired,
      poorPerformers: PropTypes.number.isRequired,
    }).isRequired,
  }),
  loading: PropTypes.bool,
};

BellCurveChart.defaultProps = {
  bellCurveData: null,
  loading: false,
};

export default BellCurveChart;